import React, { useState } from "react";
import { Mail, MessageSquareText, Send, ShieldCheck } from "lucide-react";
import { API_BASE_URL } from "../api";
import PageHeader from "../components/PageHeader";
import { Button, Field, SectionHeader, SurfaceCard, TextareaField } from "../components/designSystem";

function Contact() {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState("");
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setSuccess("");
    setError("");
    if (!form.name || !form.message) {
      setError("Name and message are required.");
      return;
    }
    setLoading(true);
    try {
  const res = await fetch(`${API_BASE_URL}/api/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form)
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setSuccess(data.message || "Message sent!");
        setForm({ name: "", email: "", message: "" });
      } else {
        setError(data.error || "Failed to send message.");
      }
    } catch (err) {
      setError("Network error. Please try again.");
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-[#f3efe7] text-slate-950 dark:bg-[linear-gradient(180deg,_#020617_0%,_#111827_45%,_#0f172a_100%)] dark:text-slate-100">
      <PageHeader 
        title="Contact Us" 
        subtitle="Reach the MekEdge team for support, product feedback, partnerships, or enterprise conversations."
        eyebrow="Contact"
      />
      <div className="mx-auto grid max-w-6xl gap-6 px-6 py-16 lg:grid-cols-[0.9fr_1.1fr] lg:px-10">
        <SurfaceCard className="bg-[linear-gradient(135deg,_#0f172a,_#111827_58%,_#1d4ed8)] text-white dark:border-gray-700">
          <SectionHeader
            eyebrow="Support"
            title="Start the conversation with the right context"
            description="Tell us what you are trying to do, what broke, or what product direction you want to discuss so the response can be useful quickly."
          />
          <div className="mt-8 space-y-4">
            <div className="rounded-3xl border border-white/10 bg-white/10 p-4">
              <div className="flex items-center gap-3">
                <Mail className="h-5 w-5 text-sky-200" />
                <div>
                  <p className="text-sm font-semibold">General support</p>
                  <p className="mt-1 text-sm text-slate-200">Questions about your account, analytics, or subscription workflows.</p>
                </div>
              </div>
            </div>
            <div className="rounded-3xl border border-white/10 bg-white/10 p-4">
              <div className="flex items-center gap-3">
                <MessageSquareText className="h-5 w-5 text-sky-200" />
                <div>
                  <p className="text-sm font-semibold">Product feedback</p>
                  <p className="mt-1 text-sm text-slate-200">Share gaps, friction points, or the workflows serious traders still need.</p>
                </div>
              </div>
            </div>
            <div className="rounded-3xl border border-white/10 bg-white/10 p-4">
              <div className="flex items-center gap-3">
                <ShieldCheck className="h-5 w-5 text-sky-200" />
                <div>
                  <p className="text-sm font-semibold">Enterprise conversations</p>
                  <p className="mt-1 text-sm text-slate-200">Use this channel for mentor groups, trading communities, or desk-style team setups.</p>
                </div>
              </div>
            </div>
          </div>
        </SurfaceCard>

        <SurfaceCard className="bg-white dark:bg-gray-900">
          <SectionHeader
            eyebrow="Message form"
            title="Send a note to the team"
            description="This goes directly into the platform contact inbox so it can be handled inside the admin workflow."
          />
          <form className="mt-6 space-y-5" onSubmit={submit}>
            <Field
              label="Your Name"
              value={form.name}
              onChange={e => setForm({ ...form, name: e.target.value })}
              placeholder="Your name"
              required
            />
            <Field
              label="Your Email"
              type="email"
              value={form.email}
              onChange={e => setForm({ ...form, email: e.target.value })}
              placeholder="you@example.com"
            />
            <TextareaField
              label="Your Message"
              rows={7}
              value={form.message}
              onChange={e => setForm({ ...form, message: e.target.value })}
              placeholder="Describe the issue, request, or opportunity clearly."
              required
            />
            <Button type="submit" variant="brand" className="w-full" disabled={loading}>
              <Send className="h-4 w-4" />
              {loading ? "Sending..." : "Send Message"}
            </Button>
            {error ? <div className="text-sm text-rose-600" role="alert">{error}</div> : null}
            {success ? <div className="text-sm text-emerald-600" role="status">{success}</div> : null}
          </form>
        </SurfaceCard>
      </div>
    </div>
  );
}

export default Contact;